import React, { useCallback, useState } from 'react'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFilter,
  faMagnifyingGlass,
  faRefresh,
  faAnglesLeft,
  faAnglesRight,
  faChevronLeft,
  faChevronRight,
  faPen,
  faSpinnerThird,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  outlinedInputClasses,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import Button from 'src/components/era-package/forms/Button'
import ChildCard from 'src/components/era-package/shared/ChildCard'
import { Filter } from 'src/components/era-package/filters'
import SnackBar from 'src/components/era-package/era/SnackBar'
import { useServerPagination } from 'src/views/abi/hooks/useServerPagination'
import AbiHtsRuleApiUrls from 'src/apiUrls/AbiHtsRuleApiUrls'
import CreateRuleButton from './CreateRuleButton'
import HtsRuleDialog from './HtsRuleDialog'

const DEFAULT_PARAMS = { page: 0, size: 10, search: '' }

const columnHelper = createColumnHelper()

export const RulesTab = () => {
  const { data, totalElements, totalPages, loading, params, fetchPage, updateParams } =
    useServerPagination(AbiHtsRuleApiUrls.LIST, DEFAULT_PARAMS)

  const [searchInput, setSearchInput] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [columnFilters, setColumnFilters] = useState([])
  const debounceRef = React.useRef(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState('create')
  const [dialogValue, setDialogValue] = useState(null)

  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null })
  const [deleteLoading, setDeleteLoading] = useState(false)

  const [errorMessage, setErrorMessage] = useState('')
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false)

  const showApiError = useCallback((message) => {
    setErrorMessage(message || 'Internal error occurred, please try again later.')
    setShowErrorSnackbar(true)
  }, [])

  const showApiSuccess = useCallback((message) => {
    setSuccessMessage(message)
    setShowSuccessSnackbar(true)
  }, [])

  const openCreateDialog = useCallback(() => {
    setDialogMode('create')
    setDialogValue(null)
    setDialogOpen(true)
  }, [])

  const openEditDialog = useCallback((row) => {
    setDialogMode('edit')
    setDialogValue(row)
    setDialogOpen(true)
  }, [])

  const closeDialog = useCallback(() => setDialogOpen(false), [])

  const handleSave = useCallback(async (draft) => {
    const isCreate = draft.id == null
    try {
      const response = await fetch(isCreate ? AbiHtsRuleApiUrls.CREATE : AbiHtsRuleApiUrls.UPDATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
        },
        body: JSON.stringify(draft),
      })
      const json = await response.json().catch(() => null)

      if (!response.ok) {
        showApiError(json?.message)
        return
      }

      setDialogOpen(false)
      showApiSuccess(isCreate ? 'HTS Rule added.' : 'HTS Rule updated.')
      fetchPage()
    } catch (error) {
      showApiError()
    }
  }, [fetchPage, showApiError, showApiSuccess])

  const openDeleteDialog = useCallback((row) => setDeleteDialog({ open: true, row }), [])
  const closeDeleteDialog = useCallback(() => setDeleteDialog({ open: false, row: null }), [])

  const handleDelete = useCallback(async () => {
    const row = deleteDialog.row
    if (!row) return

    setDeleteLoading(true)
    try {
      const response = await fetch(`${AbiHtsRuleApiUrls.DELETE}?id=${row.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
        },
      })
      const json = await response.json().catch(() => null)

      if (!response.ok) {
        showApiError(json?.message)
        return
      }

      showApiSuccess('HTS Rule deleted.')
      fetchPage()
    } catch (error) {
      showApiError()
    } finally {
      setDeleteLoading(false)
      closeDeleteDialog()
    }
  }, [deleteDialog.row, fetchPage, showApiError, showApiSuccess, closeDeleteDialog])

  const columns = React.useMemo(() => [
    columnHelper.accessor(() => 'HTS', {
      id: 'type',
      header: () => 'Type',
      cell: () => 'HTS',
      filterFn: 'includesString',
    }),
    columnHelper.accessor('hts', {
      header: () => 'Reference',
      cell: (info) => info.getValue() ?? '-',
      filterFn: 'includesString',
    }),
    columnHelper.accessor('remark', {
      header: () => 'Remark',
      cell: (info) => info.getValue() ?? '-',
      filterFn: 'includesString',
    }),
    columnHelper.accessor('updatedDate', {
      header: () => 'Last Update Date',
      cell: (info) => {
        const value = info.getValue()
        return value ? moment(new Date(value)).format('LL, h:mm A') : '-'
      },
      filterFn: 'includesString',
    }),
    columnHelper.display({
      id: 'actions',
      header: () => 'Actions',
      cell: (info) => (
        <>
          <IconButton size="small" onClick={() => openEditDialog(info.row.original)} title="Edit">
            <FontAwesomeIcon icon={faPen} width={14} />
          </IconButton>
          <IconButton size="small" onClick={() => openDeleteDialog(info.row.original)} title="Delete">
            <FontAwesomeIcon icon={faTrash} width={14} />
          </IconButton>
        </>
      ),
    }),
  ], [openEditDialog, openDeleteDialog])

  const pageIndex = params.page
  const pageSize = params.size

  const table = useReactTable({
    data,
    columns,
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search all columns..."
          value={searchInput}
          onChange={(event) => {
            const value = event.target.value
            setSearchInput(value)
            clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
              updateParams({ search: value, page: 0 })
            }, 500)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon icon={faMagnifyingGlass} style={{ width: 16, height: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: { xs: '100%', sm: '300px' },
            [`& .${outlinedInputClasses.notchedOutline}`]: { inset: 0 },
          }}
        />

        <Button
          variant="outlined"
          onClick={() => setShowFilters((prev) => !prev)}
          startIcon={<FontAwesomeIcon icon={faFilter} style={{ width: 16, height: 16 }} />}
        >
          {showFilters ? 'Hide Filters' : 'Show Column Filters'}
        </Button>

        <Box flexGrow={1} />

        <CreateRuleButton onSelectType={openCreateDialog} />
      </Stack>

      <ChildCard sx={{ p: 0, mb: -3 }}>
        <Stack>
          <TableContainer>
            <Table>
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <React.Fragment key={headerGroup.id}>
                    <TableRow>
                      {headerGroup.headers.map((header) => (
                        <TableCell key={header.id}>
                          <Typography variant="body" color="primary.main" fontWeight="600">
                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                    {showFilters && (
                      <TableRow>
                        {headerGroup.headers.map((header) => (
                          <TableCell key={`filter-${header.id}`}>
                            {header.column.getCanFilter() ? <Filter column={header.column} /> : null}
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} hover>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} sx={{ whiteSpace: 'nowrap' }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography variant="body2" color="text.secondary">Loading...</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography variant="body2" color="text.secondary">No rules found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />

          <Stack gap={1} p={3} alignItems="center" direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => fetchPage()}
                startIcon={<FontAwesomeIcon icon={faRefresh} style={{ width: 16, height: 16 }} spin={loading} />}
              >
                Refresh data
              </Button>
              <Typography variant="body1">{totalElements} Rows</Typography>
            </Box>

            <Box sx={{ display: { xs: 'block', sm: 'flex' } }} alignItems="center" gap={1}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body1">Page</Typography>
                <Typography variant="body1" fontWeight={600}>{pageIndex + 1} of {totalPages}</Typography>
              </Stack>
              <Select
                value={pageSize}
                onChange={(event) => updateParams({ size: Number(event.target.value), page: 0 })}
                size="small"
              >
                {[10, 25, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </Select>

              <IconButton size="small" onClick={() => updateParams({ page: 0 })} disabled={pageIndex === 0}>
                <FontAwesomeIcon icon={faAnglesLeft} />
              </IconButton>
              <IconButton size="small" onClick={() => updateParams({ page: pageIndex - 1 })} disabled={pageIndex === 0}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </IconButton>
              <IconButton size="small" onClick={() => updateParams({ page: pageIndex + 1 })} disabled={pageIndex >= totalPages - 1}>
                <FontAwesomeIcon icon={faChevronRight} />
              </IconButton>
              <IconButton size="small" onClick={() => updateParams({ page: totalPages - 1 })} disabled={pageIndex >= totalPages - 1}>
                <FontAwesomeIcon icon={faAnglesRight} />
              </IconButton>
            </Box>
          </Stack>
        </Stack>
      </ChildCard>

      <HtsRuleDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValue={dialogValue}
        onClose={closeDialog}
        onSave={handleSave}
      />

      <Dialog open={deleteDialog.open} onClose={closeDeleteDialog}>
        <DialogTitle>Delete HTS Rule "{deleteDialog.row?.hts}"?</DialogTitle>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete}
            color="error"
            disabled={deleteLoading}
            endIcon={<FontAwesomeIcon icon={deleteLoading ? faSpinnerThird : faTrash} width={12} spin={deleteLoading} />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBar title="Error" type="error" open={showErrorSnackbar} onClose={() => setShowErrorSnackbar(false)} duration={10000}>
        {errorMessage || 'Internal error occurred, please try again later.'}
      </SnackBar>

      <SnackBar title="Success" type="success" open={showSuccessSnackbar} onClose={() => setShowSuccessSnackbar(false)} duration={4000}>
        {successMessage}
      </SnackBar>
    </Stack>
  )
}

export default RulesTab
